/**
 * TerrainManager — chunked terrain with LOD bands for world streaming.
 *
 * The world is divided into grid sectors (50×50m each, matching the
 * InterestManager's SECTOR_SIZE).  Each sector gets a terrain chunk
 * at one of three LOD levels based on distance from the camera:
 *
 *   LOD 0 (near)  — full-resolution mesh, 33×33 vertices per chunk
 *   LOD 1 (mid)   — medium mesh, 17×17 vertices
 *   LOD 2 (far)   — coarse mesh, 9×9 vertices
 *
 * Chunks are created/removed as the player moves across sector
 * boundaries.  Height is deterministic (seeded simplex-like noise)
 * so the server and all clients generate the same terrain without
 * needing to transmit height data.
 *
 * Design decisions:
 *   - Max 9 visible chunks (3×3 neighborhood) — matches InterestManager
 *   - Height noise uses mulberry32 PRNG seeded per-sector for determinism
 *   - Meshes are THREE.PlaneGeometry rotated flat (XZ plane)
 *   - Vertex colors add visual differentiation (grass→dirt gradient)
 */

import * as THREE from 'three';

// ─── Configuration ───────────────────────────────────────────────────

/** Matches shared/grid.ts SECTOR_SIZE. */
export const SECTOR_SIZE = 50;

/** How many sectors to render around the player (radius). */
const VISIBILITY_RADIUS = 2; // 5×5 → 250m view distance

/** LOD distance thresholds (world units from camera). */
const LOD0_DIST = SECTOR_SIZE * 1.5;  // 75m — full detail
const LOD1_DIST = SECTOR_SIZE * 3.0;  // 150m — medium
// Beyond LOD1_DIST → LOD2 (coarse)

/** Vertex counts per side for each LOD level. */
const LOD_RESOLUTION = {
  0: 33,  // ~1089 verts
  1: 17,  // ~289 verts
  2: 9,   // ~81 verts
} as const;

// ─── Noise (deterministic, single world seed — seamless across sectors) ───

/** Single world seed: all sectors share it, so heights match at borders. */
const WORLD_SEED = 0xc0e5a7;

/** mulberry32 — fast 32-bit PRNG. */
function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Simple 2-octave value noise for terrain height. Single world seed → seamless. */
function terrainHeight(wx: number, wz: number): number {
  const rng = mulberry32(WORLD_SEED);
  // Use world coords directly — each sector starts at (gx*SECTOR_SIZE, gz*SECTOR_SIZE)
  const fx = wx * 0.06;
  const fz = wz * 0.06;

  // Coarse octave
  const x0 = Math.floor(fx);
  const z0 = Math.floor(fz);
  const dx = fx - x0;
  const dz = fz - z0;

  // Hash function for corner values
  const hash = (xi: number, zi: number): number => {
    const h = ((xi * 374761393 + zi * 668265263 + WORLD_SEED) >>> 0);
    rng(); rng(); // advance RNG to decorrelate
    return ((h * 2654435761) >>> 0) / 4294967296;
  };

  const v00 = hash(x0, z0);
  const v10 = hash(x0 + 1, z0);
  const v01 = hash(x0, z0 + 1);
  const v11 = hash(x0 + 1, z0 + 1);

  // Smoothstep interpolation
  const sx = dx * dx * (3 - 2 * dx);
  const sz = dz * dz * (3 - 2 * dz);

  const coarse = (1 - sx) * (1 - sz) * v00 + sx * (1 - sz) * v10 +
                 (1 - sx) * sz * v01 + sx * sz * v11;

  // Fine octave
  const fx2 = wx * 0.18;
  const fz2 = wz * 0.18;
  const x1 = Math.floor(fx2);
  const z1 = Math.floor(fz2);
  const dx2 = fx2 - x1;
  const dz2 = fz2 - z1;
  const sx2 = dx2 * dx2 * (3 - 2 * dx2);
  const sz2 = dz2 * dz2 * (3 - 2 * dz2);

  const h2 = (xi: number, zi: number): number => {
    const h = ((xi * 661469879 + zi * 722145791 + WORLD_SEED + 31337) >>> 0);
    return ((h * 2654435761) >>> 0) / 4294967296;
  };

  const v2_00 = h2(x1, z1);
  const v2_10 = h2(x1 + 1, z1);
  const v2_01 = h2(x1, z1 + 1);
  const v2_11 = h2(x1 + 1, z1 + 1);

  const fine = (1 - sx2) * (1 - sz2) * v2_00 + sx2 * (1 - sz2) * v2_10 +
               (1 - sx2) * sz2 * v2_01 + sx2 * sz2 * v2_11;

  // Combine octaves: coarse terrain + fine detail
  const height = coarse * 4.0 + fine * 1.5;

  // Flat construction yard at origin: full flat within 18 m, blends out by 30 m.
  const distOrigin = Math.sqrt(wx * wx + wz * wz);
  const PAD_R = 18, BLEND_R = 30;
  if (distOrigin < BLEND_R) {
    const t = distOrigin <= PAD_R ? 0 : (distOrigin - PAD_R) / (BLEND_R - PAD_R);
    const smooth = t * t * (3 - 2 * t);
    const padH = 1.2; // pad height — SPAWN_HEIGHT in main.ts must match
    return padH * (1 - smooth) + height * smooth;
  }
  return height;
}

// ─── Sector helpers ──────────────────────────────────────────────────

function sectorKey(gx: number, gz: number): string {
  return `${gx}:${gz}`;
}

function sectorAt(x: number, z: number): { gx: number; gz: number } {
  return {
    gx: Math.floor(x / SECTOR_SIZE),
    gz: Math.floor(z / SECTOR_SIZE),
  };
}

/** Deterministic seed for a sector. */
function sectorSeed(gx: number, gz: number): number {
  // Combine sector coords into a 32-bit seed
  return ((gx * 73856093) ^ (gz * 19349663)) >>> 0;
}

// ─── Per-chunk state ─────────────────────────────────────────────────

interface TerrainChunk {
  /** Sector key (e.g. "0:0"). */
  key: string;
  /** Grid coords. */
  gx: number;
  gz: number;
  /** Current LOD level. */
  lod: keyof typeof LOD_RESOLUTION;
  /** The THREE.Mesh for this chunk. */
  mesh: THREE.Mesh;
}

// ─── TerrainManager ──────────────────────────────────────────────────

export class TerrainManager {
  private scene: THREE.Scene;
  /** Currently active chunks, keyed by sector key. */
  private chunks = new Map<string, TerrainChunk>();
  /** Last known player position. */
  private lastSector: { gx: number; gz: number } | null = null;

  /** Material pool — one per LOD level for batching. */
  private materials: THREE.MeshStandardMaterial[];

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    this.materials = [
      // LOD 0 — white base lets vertex colors (grass→dirt) show as authored
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.9,
        metalness: 0.0,
        vertexColors: true,
      }),
      // LOD 1 — simpler
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.95,
        metalness: 0.0,
      }),
      // LOD 2 — simplest, flat color
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 1.0,
        metalness: 0.0,
      }),
    ];
  }

  // ─── Public API ─────────────────────────────────────────────────

  /**
   * Call every frame with the player's current position.
   * Creates/removes/upgrades terrain chunks as needed.
   */
  update(playerX: number, playerZ: number): void {
    const currentSector = sectorAt(playerX, playerZ);
    const sectorChanged =
      !this.lastSector ||
      this.lastSector.gx !== currentSector.gx ||
      this.lastSector.gz !== currentSector.gz;
    this.lastSector = currentSector;

    if (sectorChanged) {
      // Compute visible sectors (5×5 or configurable radius)
      const visible = new Set<string>();
      for (let dx = -VISIBILITY_RADIUS; dx <= VISIBILITY_RADIUS; dx++) {
        for (let dz = -VISIBILITY_RADIUS; dz <= VISIBILITY_RADIUS; dz++) {
          visible.add(sectorKey(currentSector.gx + dx, currentSector.gz + dz));
        }
      }

      // Remove chunks that are no longer visible
      for (const [key, chunk] of this.chunks) {
        if (!visible.has(key)) {
          this.removeChunk(chunk);
        }
      }

      // Add missing visible chunks (LOD assigned below)
      for (const key of visible) {
        if (!this.chunks.has(key)) {
          const [gsx, gsz] = key.split(':').map(Number);
          this.createChunk(gsx, gsz, 2); // placeholder LOD, corrected below
        }
      }
    }

    // Refresh LOD every update — distance bands can shift without a
    // sector crossing (e.g. walking 40 m within one 50 m sector).
    for (const chunk of this.chunks.values()) {
      const dist = this.chunkDistance(chunk.gx, chunk.gz, playerX, playerZ);
      const targetLod = this.lodForDistance(dist);
      if (chunk.lod !== targetLod) {
        const { gx, gz } = chunk;
        this.removeChunk(chunk);
        this.createChunk(gx, gz, targetLod);
      }
    }
  }

  /**
   * Get the terrain height at a world position (for physics/placement).
   * Returns 0 if chunk isn't loaded.
   */
  getHeightAt(wx: number, wz: number): number {
    return terrainHeight(wx, wz);
  }

  /** Total number of active chunks. */
  get chunkCount(): number {
    return this.chunks.size;
  }

  /** Total triangle count across all chunks. */
  get triangleCount(): number {
    let count = 0;
    for (const chunk of this.chunks.values()) {
      const geo = chunk.mesh.geometry as THREE.BufferGeometry;
      const idx = geo.index;
      if (idx) {
        count += idx.count / 3;
      } else {
        const pos = geo.getAttribute('position');
        if (pos) count += pos.count / 3;
      }
    }
    return Math.round(count);
  }

  /** Dispose all chunks (for cleanup). */
  dispose(): void {
    for (const chunk of this.chunks.values()) {
      this.removeChunk(chunk);
    }
    for (const mat of this.materials) {
      mat.dispose();
    }
  }

  // ─── Private ────────────────────────────────────────────────────

  private lodForDistance(dist: number): keyof typeof LOD_RESOLUTION {
    if (dist <= LOD0_DIST) return 0;
    if (dist <= LOD1_DIST) return 1;
    return 2;
  }

  /** Distance from sector center to player position. */
  private chunkDistance(gx: number, gz: number, px: number, pz: number): number {
    const cx = (gx + 0.5) * SECTOR_SIZE;
    const cz = (gz + 0.5) * SECTOR_SIZE;
    return Math.sqrt((cx - px) ** 2 + (cz - pz) ** 2);
  }

  private createChunk(gx: number, gz: number, lod: keyof typeof LOD_RESOLUTION): void {
    const key = sectorKey(gx, gz);
    if (this.chunks.has(key)) return;

    const res = LOD_RESOLUTION[lod];
    // Chunk center in world space — heights are baked in world coords,
    // so the mesh itself stays at the origin.
    const cx = gx * SECTOR_SIZE + SECTOR_SIZE / 2;
    const cz = gz * SECTOR_SIZE + SECTOR_SIZE / 2;

    // Build geometry
    const geo = new THREE.PlaneGeometry(SECTOR_SIZE, SECTOR_SIZE, res - 1, res - 1);
    geo.rotateX(-Math.PI / 2); // Lay flat on XZ plane: verts are (x, 0, z), up is +Y

    // Set heights
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const colors = new Float32Array(pos.count * 3);

    for (let i = 0; i < pos.count; i++) {
      const wx = cx + pos.getX(i);
      const wz = cz + pos.getZ(i);

      const h = terrainHeight(wx, wz);
      pos.setY(i, h); // Y is up after rotation

      // Vertex colors: grass (green) at low height → dirt (brown) higher
      const t = Math.min(h / 5.0, 1.0);
      colors[i * 3] = 0.33 + t * 0.47;      // R: 0.33→0.80
      colors[i * 3 + 1] = 0.87 - t * 0.33;   // G: 0.87→0.54
      colors[i * 3 + 2] = 0.33 - t * 0.13;   // B: 0.33→0.20
    }

    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.computeVertexNormals();

    const mesh = new THREE.Mesh(geo, this.materials[lod]);
    mesh.position.set(cx, 0, cz); // chunk origin → world; verts are local
    mesh.receiveShadow = true;
    mesh.castShadow = lod === 0; // Only near chunks cast shadows

    this.scene.add(mesh);

    const chunk: TerrainChunk = { key, gx, gz, lod, mesh };
    this.chunks.set(key, chunk);
  }

  private removeChunk(chunk: TerrainChunk): void {
    this.scene.remove(chunk.mesh);
    chunk.mesh.geometry.dispose();
    this.chunks.delete(chunk.key);
  }
}